import React, { useEffect, useState, type FC } from 'react';
import Container from '../../container';
import Title from '../../title';
import { Flex, Grid, Paragraph } from '../../UIKit/system';
import Button from '../../button';
import CE from '../../../images/svg/CE.svg';
import UKCA from '../../../images/svg/UKCA.svg';
import type { StoryblokProductComponentProps } from '../../storyblok/storyblok-product-component';
import LazyImage from '../../lazy-image';
import styled from 'styled-components';
import { getThemeColor } from '../../../utils/styles';
import { getPath } from '../../../utils/paths';
import { ProductGallery } from '../gallery';
import { CustomisationModal } from '../../customisation-modal';
import DownloadPopup from '../../resource-centre/download-popup';
import { getFluidGatsbyImage } from '../../../utils/gatsby-storyblok-image-fixed';

interface ProductHeroProps {
  title: string;
  text?: string;
  showSizes?: boolean;
  ceIcon?: boolean;
  ukcaIcon?: boolean;
  dataSheet?: string;
  systems?: StoryblokProductComponentProps['systemTypes'];
  customisationFeature?: StoryblokProductComponentProps['customisationFeature'];
  customisationModal: {
    label?: string;
    title?: string;
    image?: Record<any, any>;
    content?: any;
  };
  leadTimesStock?: string;
  leadTimesOrder?: string;
  deliveryMessage?: string;
  gallery: StoryblokProductComponentProps['gallery'];
}

export const ProductHero: FC<ProductHeroProps> = (props) => {
  const {
    title,
    text,
    systems,
    dataSheet,
    showSizes,
    customisationModal,
    leadTimesStock,
    leadTimesOrder,
    deliveryMessage,
    customisationFeature,
    gallery,
    ceIcon,
    ukcaIcon
  } = props;
  const [showCustomModal, setShowCustomModal] = useState(false);

  useEffect(() => {
    const body = document.getElementsByTagName('body')[0];

    if (body && showCustomModal) {
      body.style.overflow = 'hidden';
    } else if (body) {
      body.style.overflow = '';
    }
  }, [showCustomModal]);

  return (
    <div>
      <Container large py={[36, 72]}>
        <HeroGrid>
          <div>
            <Title as="h1" mb={24}>
              {title}
            </Title>
            {text && (
              <Paragraph mb={28} color="grey">
                {text}
              </Paragraph>
            )}

            {systems && systems.length > 0 && (
              <Grid gridAutoFlow="row" pb={32}>
                <Subtitle>System type</Subtitle>
                <Flex flexWrap="wrap" gap={32} mt={12}>
                  {systems.slice(0, 3).map((system) => (
                    <StyledSystem key={system._uid}>
                      <LazyImage
                        fluid={getFluidGatsbyImage(system.image)}
                        width={32}
                        height={32}
                      />
                      {system.title}
                    </StyledSystem>
                  ))}
                </Flex>
              </Grid>
            )}

            {(showSizes ||
              customisationFeature ||
              !!customisationModal.content) && (
              <Grid gridAutoFlow="row" pb={32}>
                <Subtitle>Customisations available</Subtitle>
                <Flex pt={6} gap={10}>
                  {showSizes && (
                    <StyledCustomisation>
                      <CustomisationButton to="#size-guide">
                        Sizes
                      </CustomisationButton>
                    </StyledCustomisation>
                  )}
                  {customisationFeature && (
                    <StyledCustomisation>
                      <CustomisationButton to="#overview">
                        {customisationFeature}
                      </CustomisationButton>
                    </StyledCustomisation>
                  )}
                  {!!customisationModal.content && (
                    <StyledCustomisation>
                      <CustomisationButton
                        to="#"
                        onClick={(event) => {
                          event.preventDefault();
                          setShowCustomModal(true);
                        }}
                      >
                        {customisationModal.label}
                      </CustomisationButton>
                    </StyledCustomisation>
                  )}
                </Flex>
              </Grid>
            )}

            {(leadTimesStock || leadTimesOrder || deliveryMessage) && (
              <LeadDeliveryGrid>
                {(leadTimesStock || leadTimesOrder) && (
                  <div>
                    <Subtitle>Lead times</Subtitle>
                    {leadTimesStock && (
                      <Text>
                        <strong>Stock:</strong> {leadTimesStock}
                      </Text>
                    )}
                    {leadTimesOrder && (
                      <Text>
                        <strong>Made to order:</strong> {leadTimesOrder}
                      </Text>
                    )}
                  </div>
                )}
                <StyledDelivery>
                  <Subtitle>Dedicated Sertus delivery</Subtitle>
                  <Text>{deliveryMessage}</Text>
                </StyledDelivery>
              </LeadDeliveryGrid>
            )}

            <ButtonsGrid>
              <Button to={getPath.contact()}>Get a quote</Button>
              <DownloadDataSheetButton file={dataSheet} />
            </ButtonsGrid>
          </div>
          <Grid gridTemplateRows="1fr auto">
            <ProductGallery items={gallery} />
            <Grid
              gridAutoFlow="column"
              justifyContent="end"
              pt={32}
              gridGap={12}
            >
              {ceIcon && (
                <StyledIcon>
                  <CE />
                </StyledIcon>
              )}
              {ukcaIcon && (
                <StyledIcon>
                  <UKCA />
                </StyledIcon>
              )}
            </Grid>
          </Grid>
        </HeroGrid>
      </Container>

      {showCustomModal && customisationModal.content && (
        <CustomisationModal
          {...customisationModal}
          content={customisationModal.content}
          onClose={() => setShowCustomModal(false)}
        />
      )}
    </div>
  );
};

const HeroGrid = styled(Grid).attrs(() => ({
  gridAutoFlow: ['row', 'row', 'column'],
  gridTemplateColumns: [undefined, undefined, 'repeat(2, 1fr)'],
  gap: 30
}))``;

const Subtitle = styled(Paragraph).attrs(() => ({
  as: 'p',
  fontSizeSmall: true,
  fontWeight: 700,
  pb: 6
}))``;

const Text = styled(Paragraph).attrs(() => ({
  color: 'grey',
  fontSizeSmall: true
}))``;

const StyledSystem = styled(Grid).attrs(() => ({
  gridAutoFlow: 'column',
  gridGap: 5,
  alignItems: 'center',
  color: 'grey'
}))``;

const StyledCustomisation = styled(Grid).attrs(() => ({
  alignItems: 'center',
  gridAutoFlow: 'column'
}))`
  &::before {
    content: '';
    display: inline-grid;
    border-left: 2px ${getThemeColor('grey2')} solid;
    height: 70%;
    margin-right: 10px;
  }

  &:first-child&::before {
    content: none;
  }
`;

const CustomisationButton = styled(Button).attrs(() => ({
  primaryTransparent: true,
  fontSize: 16
}))``;

const LeadDeliveryGrid = styled(Grid).attrs(() => ({
  gridAutoFlow: ['row', 'column'],
  gridGap: [30, 60],
  justifyContent: 'start',
  pb: 23
}))``;

const StyledDelivery = styled.div`
  width: 200px;
`;

const StyledIcon = styled(Grid)`
  align-items: center;
  background-color: ${getThemeColor('lightGrey')};
  height: 48px;
  justify-items: center;
  width: 48px;
`;

const ButtonsGrid = styled(Grid).attrs(() => ({
  gridTemplateColumns: [undefined, 'repeat(2, 1fr)', 'auto auto'],
  justifyContent: [undefined, 'start'],
  gap: 20,
  pt: 23
}))``;

const DownloadDataSheetButton: FC<{ file: string }> = ({ file }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        to="#"
        onClick={(event) => {
          event.preventDefault();
          setIsOpen(true);
        }}
        transparentInverted
      >
        Download Datasheet
      </Button>
      {isOpen && (
        <DownloadPopup
          filePath={file}
          handlePopupClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
